"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { isSuccess } from "../../../models/response/base-response";
import RewashReportService, {
  RewashDrilldownKey,
  RewashOrderRow,
} from "../services/rewash-report-service";

const numberFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const weightFmt = new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const money = (v: number) => `${numberFmt.format(Number(v) || 0)}đ`;
const kg = (v: number) => `${weightFmt.format(Number(v) || 0)} kg`;
/**
 * `2026-08-17 02:54:05` → `17/08/2026`, kèm `withTime` thì thêm ` 02:54`.
 * Cắt chuỗi chứ không `new Date()`: giờ BE trả là giờ VN sẵn, parse lại theo tz trình duyệt sẽ lệch.
 * Vẫn chịu được dạng ISO `...T02:54:05.000Z` phòng khi có API khác chưa chuẩn hoá.
 */
const dmy = (s: string, withTime = false) => {
  if (!s) return "—";
  const [datePart, timePart = ""] = s.replace("T", " ").replace("Z", "").trim().split(" ");
  const [y, m, dd] = (datePart || "").split("-");
  if (!dd || !m || !y) return s;
  const date = `${dd}/${m}/${y}`;
  return withTime && timePart ? `${date} ${timePart.slice(0, 5)}` : date;
};

const PAGE_SIZE = 20;

interface Props {
  /** Tiêu đề dòng vừa bấm — VD "Chi nhánh quận 1" */
  title: string;
  /** Khoá bóc tách: đúng một trong branch_id / reason_code / customer_id / employee_id */
  filterKey: RewashDrilldownKey;
  fromDate: string;
  toDate: string;
  isPickupLaundry?: number | "";
  onClose: () => void;
}

/**
 * Danh sách đơn giặt lại đứng sau MỘT dòng của báo cáo. Dùng đúng bộ lọc kỳ + loại phát hiện của báo cáo
 * và cùng quy tắc quy chi nhánh theo NƠI GIẶT, nên tổng ở đây khớp với con số trên bảng tổng hợp.
 */
const RewashOrdersModal: React.FC<Props> = ({
  title, filterKey, fromDate, toDate, isPickupLaundry, onClose,
}) => {
  const [rows, setRows] = useState<RewashOrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const { fetch: fetchOrders } = RewashReportService.orders();
  const fetchRef = useRef(fetchOrders);
  useEffect(() => { fetchRef.current = fetchOrders; }, [fetchOrders]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchRef.current({
        from_date: fromDate,
        to_date: toDate,
        ...(isPickupLaundry === 0 || isPickupLaundry === 1 ? { is_pickup_laundry: Number(isPickupLaundry) } : {}),
        ...filterKey,
        page,
        limit: PAGE_SIZE,
      });
      if (res && isSuccess(res)) {
        setRows(res.data?.list || []);
        setTotal(Number(res.data?.total_record) || 0);
      }
    } catch (e) {
      console.error("Failed to load rewash orders:", e);
    } finally {
      setIsLoading(false);
    }
    // `filterKey` là object literal dựng lại mỗi render ở component cha → phụ thuộc theo GIÁ TRỊ đã tuần tự
    // hoá, nếu không effect chạy vô hạn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, isPickupLaundry, page, JSON.stringify(filterKey)]);

  useEffect(() => { void load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[88vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div>
            <h3 className="text-base font-bold text-slate-800">Đơn giặt lại · {title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {dmy(fromDate)} – {dmy(toDate)} · {numberFmt.format(total)} đơn
              {isPickupLaundry === 0 ? " · chỉ phát hiện ngay" : isPickupLaundry === 1 ? " · chỉ phát hiện sau" : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-2xl leading-none px-2"
            aria-label="Đóng"
          >
            ×
          </button>
        </div>

        <div className="overflow-auto flex-1">
          {isLoading ? (
            <div className="p-10 text-center text-slate-500">Đang tải...</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-slate-500">Không có đơn nào.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 sticky top-0">
                <tr>
                  {["Mã đơn", "Ngày tạo", "Đơn gốc", "Khách hàng", "Nơi giặt", "Lý do", "Phát hiện", "KL", "Giá trị", "Miễn/giảm", "NV chịu TN"].map((h, i) => (
                    <th key={h} className={`px-3 py-2 whitespace-nowrap ${i === 0 ? "text-left" : "text-center"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-800 whitespace-nowrap">{r.code}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">{dmy(r.created_at, true)}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">{r.origin_code || "—"}</td>
                    <td className="px-3 py-2">{r.customer_name || "—"}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      {r.wash_branch_name || "—"}
                      {r.is_transferred && (
                        <span
                          title={`Đơn của ${r.owner_branch_name} — điều chuyển sang đây giặt`}
                          className="ml-1 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                        >
                          giặt hộ
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">{r.reason_label}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                      {Number(r.is_pickup_laundry) === 1 ? "Sau" : "Ngay"}
                    </td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">{kg(r.weight)}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">{money(r.gross)}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap text-rose-600">{money(r.discount)}</td>
                    <td className="px-3 py-2 text-center">
                      {r.responsible_names.length ? r.responsible_names.join(", ") : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t text-sm text-slate-600">
          <span>Trang {page}/{totalPages}</span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1 || isLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-md border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
            >
              Trước
            </button>
            <button
              type="button"
              disabled={page >= totalPages || isLoading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="rounded-md border border-slate-200 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
            >
              Sau
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RewashOrdersModal;
